import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { CarrierDetailsModal } from '../../components/transport/CarrierDetailsModal';
import transportService, { type Carrier } from '../../services/transportService';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export default function Carriers() {
  const { t } = useTranslation();
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string|null>(null);

  // Form state for adding new carrier
  const [newCarrier, setNewCarrier] = useState({ name: '', email: '', phone: '' });

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Details modal state (new)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);

  // Edit modal state (old - keep for compatibility)
  const [editingCarrier, setEditingCarrier] = useState<Carrier | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', is_active: true });

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await transportService.listCarriers(true as any); // all carriers
      setCarriers(rows);
    } catch (e: any) {
      setError(e?.message || 'Błąd ładowania');
      toast.error('Nie udało się załadować przewoźników');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    if (!newCarrier.name.trim()) {
      toast.error('Nazwa przewoźnika jest wymagana');
      return;
    }

    try {
      await transportService.createCarrier(newCarrier);
      toast.success('Przewoźnik został dodany');
      setNewCarrier({ name: '', email: '', phone: '' });
      await load();
    } catch (error: any) {
      toast.error(error?.message || 'Nie udało się dodać przewoźnika');
    }
  };

  const handleViewDetails = async (carrier: Carrier) => {
    try {
      // Pobierz pełne dane przewoźnika z bazy (w tym spedytorów i kierowców)
      const fullCarrierData = await transportService.getCarrier(carrier.id);
      setSelectedCarrier(fullCarrierData);
      setDetailsModalOpen(true);
    } catch (error: any) {
      toast.error(error?.message || 'Nie udało się pobrać danych przewoźnika');
    }
  };

  const handleEdit = (carrier: Carrier) => {
    setEditingCarrier(carrier);
    setEditForm({
      name: carrier.name,
      email: carrier.email || '',
      phone: carrier.phone || '',
      is_active: carrier.is_active
    });
  };

  const handleSaveEdit = async () => {
    if (!editingCarrier) return;

    if (!editForm.name.trim()) {
      toast.error('Nazwa przewoźnika jest wymagana');
      return;
    }

    try {
      await transportService.updateCarrier(editingCarrier.id, editForm);
      toast.success('Przewoźnik został zaktualizowany');
      setEditingCarrier(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || 'Nie udało się zaktualizować przewoźnika');
    }
  };

  const handleDelete = async (carrier: Carrier) => {
    if (!confirm(`Czy na pewno chcesz TRWALE USUNĄĆ przewoźnika "${carrier.name}"?\n\nTej operacji nie można cofnąć!`)) {
      return;
    }

    try {
      await transportService.deleteCarrier(carrier.id);
      toast.success('Przewoźnik został usunięty z bazy danych');
      await load();
    } catch (error: any) {
      toast.error(error?.message || 'Nie udało się usunąć przewoźnika');
    }
  };

  if (loading) return <div className="p-6">{t('common.loading', 'Ładowanie...')}</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  // Filter carriers by search query
  const filteredCarriers = carriers.filter(carrier =>
    carrier.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    carrier.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    carrier.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    carrier.nip?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 p-6">
      {/* Add new carrier form */}
      <Card>
        <CardHeader>
          <CardTitle>{t('transport.carriers.addTitle', 'Dodaj przewoźnika')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              className="border rounded px-3 py-2"
              placeholder={t('transport.fields.name', 'Nazwa') + ' *'}
              value={newCarrier.name}
              onChange={e => setNewCarrier(p => ({ ...p, name: e.target.value }))}
            />
            <input
              className="border rounded px-3 py-2"
              placeholder={t('transport.fields.email', 'Email')}
              value={newCarrier.email}
              onChange={e => setNewCarrier(p => ({ ...p, email: e.target.value }))}
            />
            <input
              className="border rounded px-3 py-2"
              placeholder={t('transport.fields.phone', 'Telefon')}
              value={newCarrier.phone}
              onChange={e => setNewCarrier(p => ({ ...p, phone: e.target.value }))}
            />
            <Button onClick={handleAdd} className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200">
              <svg className="w-4 h-4 mr-2 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('common.add', 'Dodaj')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Carriers list table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('transport.carriers.title', 'Lista Przewoźników')} ({filteredCarriers.length})</CardTitle>
            <div className="relative w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Szukaj przewoźnika..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    {t('transport.fields.name', 'Nazwa')}
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    {t('transport.fields.email', 'Email')}
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    {t('transport.fields.phone', 'Telefon')}
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    NIP
                  </th>
                  <th className="px-4 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCarriers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      {searchQuery ? 'Nie znaleziono przewoźników pasujących do wyszukiwania' : 'Brak przewoźników'}
                    </td>
                  </tr>
                ) : (
                  filteredCarriers.map(carrier => (
                    <tr key={carrier.id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {carrier.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {carrier.email || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {carrier.phone || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {carrier.nip || '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleViewDetails(carrier)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                          >
                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Podgląd i edycja
                          </button>
                          <button
                            onClick={() => handleDelete(carrier)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                          >
                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Usuń
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Carrier Details Modal (new) */}
      <CarrierDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        carrier={selectedCarrier}
        onSuccess={load}
      />

      {/* Edit modal (old - kept for compatibility) */}
      {editingCarrier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {t('transport.carriers.editTitle', 'Edytuj przewoźnika')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('transport.fields.name', 'Nazwa')} *
                </label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.name}
                  onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('transport.fields.email', 'Email')}
                </label>
                <input
                  type="email"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.email}
                  onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('transport.fields.phone', 'Telefon')}
                </label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={editForm.phone}
                  onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  className="mr-2"
                  checked={editForm.is_active}
                  onChange={e => setEditForm(p => ({ ...p, is_active: e.target.checked }))}
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  {t('transport.fields.active', 'Aktywny')}
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setEditingCarrier(null)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                {t('common.cancel', 'Anuluj')}
              </button>
              <Button onClick={handleSaveEdit}>
                {t('common.save', 'Zapisz')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
